import { Box, Icon } from '@dev-spendesk/grapes';
import classnames from 'classnames';
import { type MonetaryValue, negate } from 'ezmoney';

import { CardPayment } from 'src/core/common/components/CardPayment/CardPayment';
import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import {
  monetaryValuesHaveSameCurrency,
  sumMonetaryValues,
} from 'src/core/common/utils/monetaryValue';
import { useGoToPayableDetails } from 'src/core/modules/payable/hooks/routes/useGoToPayableDetails';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

type CreditNotes = {
  id: string;
  availableCredit: MonetaryValue;
  creditNoteNumber?: string;
};

type Props = {
  creditNotes: CreditNotes[] | null;
};

export const AccountPayableSuppliersCreditNotesSection = ({
  creditNotes,
}: Props) => {
  const { t } = useTranslation('global');
  const goToPayable = useGoToPayableDetails();

  if (!creditNotes?.length) {
    return null;
  }

  return (
    <section>
      <Box variant="secondary" className="min-h-[80px] items-center">
        <h3 className="mb-16 text-content-primary title-l">
          {t('bookkeep.accountsPayable.panel.creditNotesSection.title')}
        </h3>

        {getPreviews(creditNotes, goToPayable, t)}
      </Box>
    </section>
  );
};

const getPreviews = (
  creditNotes: CreditNotes[] | null,
  goToPayable: (payableId: string) => void,
  t: TGlobalFunctionTyped,
) => {
  if (!creditNotes || creditNotes.length === 0) {
    return [];
  }

  const allCreditNotesHaveSameCurrency = monetaryValuesHaveSameCurrency(
    creditNotes.map((creditNote) => creditNote.availableCredit),
  );

  const previews = creditNotes.map((creditNote, index) => {
    return (
      <CardPayment
        key={creditNote.id}
        className={classnames('flex items-center justify-between gap-12', {
          'mb-8': index < creditNotes.length - 1,
        })}
        primarySlot={formatMonetaryValue(creditNote.availableCredit)}
        secondarySlot={
          creditNote.creditNoteNumber ? `#${creditNote.creditNoteNumber}` : ''
        }
        tertiarySlot={<Icon name="chevron-right" />}
        onClick={() => goToPayable(creditNote.id)}
      />
    );
  });

  if (allCreditNotesHaveSameCurrency) {
    const totalAvailableCredit = sumMonetaryValues(
      creditNotes[0].availableCredit.currency,
      creditNotes.map((creditNote) => creditNote.availableCredit),
    );

    const availableCreditPreview = (
      <div className="mt-16 flex w-full flex-row items-center gap-24">
        <span className="flex-1 text-content-secondary-bg-primary">
          {t('bookkeep.accountsPayable.panel.creditNotesSection.sumTitle')}
        </span>
        <span className="truncate text-right text-content-primary">
          {formatMonetaryValue(negate(totalAvailableCredit))}
        </span>
      </div>
    );

    previews.push(availableCreditPreview);
  }

  return previews;
};
